import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { DashboardLayout, Card } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { adminApi } from '../services/api';

const MAX_CHARS = 2048;

type TabType = 'company' | 'lawyer' | 'inspector' | 'person' | 'admin' | 'extra';

const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, 4, 5, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'color': [] }, { 'background': [] }],
    ['link'],
    ['clean']
  ]
};

const quillFormats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'list', 'bullet',
  'color', 'background',
  'link'
];

export function AdminSystemInfo() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabType>('company');
  const [companyinfo, setCompanyinfo] = useState('');
  const [lawyerinfo, setLawyerinfo] = useState('');
  const [inspectorinfo, setInspectorinfo] = useState('');
  const [personinfo, setPersoninfo] = useState('');
  const [admininfo, setAdmininfo] = useState('');
  const [extraimportant, setExtraimportant] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadSystemInfo();
  }, []);

  const loadSystemInfo = async () => {
    try {
      const data = await adminApi.getSystemInfo();
      // Handle migration from old field name
      setCompanyinfo((data as any).companyinfo || (data as any).introinfo || '');
      setLawyerinfo((data as any).lawyerinfo || '');
      setInspectorinfo((data as any).inspectorinfo || '');
      setPersoninfo((data as any).personinfo || '');
      setAdmininfo((data as any).admininfo || '');
      setExtraimportant(data.extraimportant || '');
    } catch (error) {
      console.error('Failed to load system info:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getPlainTextLength = (html: string): number => {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return (temp.textContent || temp.innerText || '').length;
  };

  const handleSave = async () => {
    // Validate all field lengths
    const fields = [
      { name: t('admin.systemInfo.tabs.company'), value: companyinfo },
      { name: t('admin.systemInfo.tabs.lawyer'), value: lawyerinfo },
      { name: t('admin.systemInfo.tabs.inspector'), value: inspectorinfo },
      { name: t('admin.systemInfo.tabs.person'), value: personinfo },
      { name: t('admin.systemInfo.tabs.admin'), value: admininfo },
      { name: t('admin.systemInfo.tabs.extra'), value: extraimportant }
    ];

    for (const field of fields) {
      if (getPlainTextLength(field.value) > MAX_CHARS) {
        setSaveMessage({ type: 'error', text: `${field.name} exceeds ${MAX_CHARS} characters` });
        return;
      }
    }

    setIsSaving(true);
    setSaveMessage(null);

    try {
      await adminApi.updateSystemInfo({
        companyinfo,
        lawyerinfo,
        inspectorinfo,
        personinfo,
        admininfo,
        extraimportant
      });
      setSaveMessage({ type: 'success', text: t('admin.systemInfo.saveSuccess') });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      console.error('Failed to save system info:', error);
      setSaveMessage({ type: 'error', text: t('admin.systemInfo.saveError') });
    } finally {
      setIsSaving(false);
    }
  };

  const renderEditor = (value: string, onChange: (value: string) => void, label: string, helpText: string) => (
    <Card>
      <div className="card-header">
        <h5 className="card-title mb-0">{label}</h5>
      </div>
      <div className="card-body">
        <p className="text-muted small mb-3">{helpText}</p>
        <ReactQuill
          theme="snow"
          value={value}
          onChange={onChange}
          modules={quillModules}
          formats={quillFormats}
          style={{ minHeight: '200px' }}
        />
        <div className="mt-2 text-end">
          <small className={getPlainTextLength(value) > MAX_CHARS ? 'text-danger' : 'text-muted'}>
            {getPlainTextLength(value)} / {MAX_CHARS} {t('common.characters') || 'tecken'}
          </small>
        </div>
      </div>
    </Card>
  );

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">{t('admin.systemInfo.title')}</h5>
          </div>
          <Breadcrumb items={[
            { label: t('nav.home'), path: '/admin/home' },
            { label: t('admin.systemInfo.title') }
          ]} />
        </div>
      </div>

      {saveMessage && (
        <div className={`alert alert-${saveMessage.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show`} role="alert">
          {saveMessage.text}
          <button type="button" className="btn-close" onClick={() => setSaveMessage(null)}></button>
        </div>
      )}

      {/* Tabs Navigation */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div className="nav-tabs-wrapper">
          <ul className="nav nav-tabs nav-tabs-custom-style" role="tablist">
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'company' ? 'active' : ''}`}
                onClick={() => setActiveTab('company')}
              >
                {t('admin.systemInfo.tabs.company')}
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'lawyer' ? 'active' : ''}`}
                onClick={() => setActiveTab('lawyer')}
              >
                {t('admin.systemInfo.tabs.lawyer')}
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'inspector' ? 'active' : ''}`}
                onClick={() => setActiveTab('inspector')}
              >
                {t('admin.systemInfo.tabs.inspector')}
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'person' ? 'active' : ''}`}
                onClick={() => setActiveTab('person')}
              >
                {t('admin.systemInfo.tabs.person')}
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'admin' ? 'active' : ''}`}
                onClick={() => setActiveTab('admin')}
              >
                {t('admin.systemInfo.tabs.admin')}
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'extra' ? 'active' : ''}`}
                onClick={() => setActiveTab('extra')}
              >
                {t('admin.systemInfo.tabs.extra')}
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {/* Company Tab */}
        <div className={`tab-pane fade ${activeTab === 'company' ? 'show active' : ''}`}>
          {renderEditor(
            companyinfo,
            setCompanyinfo,
            t('admin.systemInfo.companyLabel'),
            t('admin.systemInfo.companyHelp')
          )}
        </div>

        {/* Lawyer Tab */}
        <div className={`tab-pane fade ${activeTab === 'lawyer' ? 'show active' : ''}`}>
          {renderEditor(
            lawyerinfo,
            setLawyerinfo,
            t('admin.systemInfo.lawyerLabel'),
            t('admin.systemInfo.lawyerHelp')
          )}
        </div>

        {/* Inspector Tab */}
        <div className={`tab-pane fade ${activeTab === 'inspector' ? 'show active' : ''}`}>
          {renderEditor(
            inspectorinfo,
            setInspectorinfo,
            t('admin.systemInfo.inspectorLabel'),
            t('admin.systemInfo.inspectorHelp')
          )}
        </div>

        {/* Person Tab */}
        <div className={`tab-pane fade ${activeTab === 'person' ? 'show active' : ''}`}>
          {renderEditor(
            personinfo,
            setPersoninfo,
            t('admin.systemInfo.personLabel'),
            t('admin.systemInfo.personHelp')
          )}
        </div>

        {/* Admin Tab */}
        <div className={`tab-pane fade ${activeTab === 'admin' ? 'show active' : ''}`}>
          {renderEditor(
            admininfo,
            setAdmininfo,
            t('admin.systemInfo.adminLabel'),
            t('admin.systemInfo.adminHelp')
          )}
        </div>

        {/* Extra Important Tab */}
        <div className={`tab-pane fade ${activeTab === 'extra' ? 'show active' : ''}`}>
          {renderEditor(
            extraimportant,
            setExtraimportant,
            t('admin.systemInfo.extraLabel'),
            t('admin.systemInfo.extraHelp')
          )}
        </div>
      </div>

      {/* Spacer for Quill toolbar */}
      <div style={{ height: '45px' }}></div>

      <div className="d-flex justify-content-end mt-4">
        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {t('common.saving') || 'Sparar...'}
            </>
          ) : (
            <>
              <i className="feather-save me-2"></i>
              {t('common.save')}
            </>
          )}
        </button>
      </div>
    </DashboardLayout>
  );
}
